/**
 * 청크 로드 실패(ChunkLoadError) 회복 — dynamic import 가 일시적으로 실패할 때. (#273)
 *
 * 배경: 8/15~16 웹 오류 알림의 대부분이 모바일 사파리·인앱 브라우저의 청크 로드
 * 실패였다. 보고된 청크 URL 은 운영에서 전부 200 으로 정상 서빙 중이었다 — 배포
 * 스큐가 아니라 모바일 회선에서 잠깐 끊긴 것이다. 그렇다면 새로고침 한 번이면
 * 살아난다. 잡는 곳이 없어 global-error 오류 화면까지 번지던 것을 여기서 멈춘다.
 *
 * 역할 분담: 회복과 보고는 **오류 바운더리가 전담**한다. 전역 핸들러
 * (`error-reporter.tsx`)는 청크 오류를 보고하지 않는다 — 같은 이벤트가
 * `web/client` 채널로 중복 알림되던 노이즈의 원인이었다.
 */

import { stashTrackedText } from './stale-deployment';

const RELOADED_AT_KEY = 'chunk-load:reloaded-at';

/** 이 시간 안에 또 실패하면 자동 새로고침 대신 오류 화면 — 무한 새로고침 방지. */
const RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

/** "다시 불러오는 중" 안내가 화면에 그려질 시간. */
const RELOAD_DELAY_MS = 500;

/** 같은 오류가 여러 경로로 들어와도 새로고침 예약은 한 번만. */
let reloadScheduled = false;

/**
 * webpack(`Loading chunk N failed`, `Loading CSS chunk`)과 네이티브 ESM
 * (사파리 `Importing a module script failed`, 크롬·파이어폭스
 * `… dynamically imported module`)의 실패 메시지를 모두 잡는다.
 */
const CHUNK_LOAD_MESSAGE =
  /Loading( CSS)? chunk \S+ failed|ChunkLoadError|Importing a module script failed|dynamically imported module/i;

/** Error 객체·문자열 어느 쪽이 와도 청크 로드 실패인지 판별합니다. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  if ((error as { name?: string }).name === 'ChunkLoadError') return true;
  const message = String((error as { message?: string }).message ?? error);
  return CHUNK_LOAD_MESSAGE.test(message);
}

/**
 * 입력을 보존하고 페이지를 한 번 새로 불러옵니다.
 *
 * @returns 새로고침이 예약됐으면(이미 예약돼 있으면) true. 쿨다운에 걸렸거나
 *   sessionStorage 를 쓸 수 없으면 false — 호출자는 오류 화면으로 넘어가고,
 *   그때만 챗에 보고해야 한다(회복 실패 건만 알린다).
 */
export function recoverFromChunkLoadError(): boolean {
  if (typeof window === 'undefined') return false;
  if (reloadScheduled) return true;

  try {
    const lastReloadedAt = Number(
      window.sessionStorage.getItem(RELOADED_AT_KEY) ?? 0
    );
    if (Date.now() - lastReloadedAt < RELOAD_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
  } catch {
    // 쿨다운을 기록할 수 없으면(시크릿 모드 등) 자동 새로고침도 하지 않는다 —
    // 무한 새로고침을 막을 방법이 없기 때문이다.
    return false;
  }

  stashTrackedText();
  reloadScheduled = true;
  window.setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
  return true;
}

/** 테스트용 — 모듈 내부 상태 초기화 */
export function __resetChunkLoadRecoveryState() {
  reloadScheduled = false;
}
